import { create } from 'zustand';
import { storage } from '@/lib/storage';
import type { Theme } from '@/lib/theme';

const STORAGE_KEY = 'devutils:prefs';
const MAX_RECENTS = 6;
const MAX_TABS = 12;

export type DockPosition = 'right' | 'bottom' | 'hidden';

export interface Tab {
  id: string; // unique tab instance id
  toolId: string;
}

interface PersistedPrefs {
  favorites: string[];
  recents: string[];
  theme: Theme;
  /** Open workspace tabs. The browser/welcome view is represented by an
   *  empty array (no active tab). */
  tabs: Tab[];
  activeTabId: string | null;
  toolState: Record<string, unknown>;
  dockPosition: DockPosition;
  /** Tools shown in the dock. `null` means "show all tools" (default for new
   *  installs); `[]` means the user opted to hide everything. Order matches
   *  the registry — we never re-arrange. */
  dockTools: string[] | null;
}

interface PrefsActions {
  /** Open a tool: focus an existing tab for it, or push a new tab. */
  openTool: (toolId: string) => void;
  /** Close a tab; advances active tab. */
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string | null) => void;
  /** Close all tabs and return to the browser view. */
  closeAllTabs: () => void;

  toggleFavorite: (id: string) => void;
  pushRecent: (id: string) => void;
  setTheme: (theme: Theme) => void;
  setToolState: <T>(toolId: string, state: T) => void;
  getToolState: <T>(toolId: string) => T | undefined;
  setDockPosition: (position: DockPosition) => void;
  setDockTools: (ids: string[] | null) => void;
  hydrate: () => Promise<void>;
}

type Store = PersistedPrefs & PrefsActions & {
  hydrated: boolean;
};

/**
 * Derive the active tool's id from tabs + activeTabId. Exposed as a selector
 * so React subscribers re-evaluate it on every relevant state change. A
 * previous version stored this as a JS getter on the store object — Zustand's
 * `set({...patch})` spread captured the getter's current value as a plain
 * property, freezing it and breaking tool-open. Don't reintroduce that.
 */
export const selectActiveToolId = (s: {
  tabs: Tab[];
  activeTabId: string | null;
}): string | null => {
  if (!s.activeTabId) return null;
  return s.tabs.find((t) => t.id === s.activeTabId)?.toolId ?? null;
};

/**
 * First-run defaults. The dock starts at the **bottom** with a curated set of
 * everyday tools — the things most users reach for first. Users can re-arrange
 * the dock (Settings → Dock) or hide it entirely afterwards; what they pick
 * sticks because hydrate prefers `stored.dockTools` over this default.
 */
const DEFAULT_DOCK_TOOLS = ['notes', 'vault', 'image', 'qr', 'password', 'textcase'];

const DEFAULTS: PersistedPrefs = {
  favorites: [],
  recents: [],
  theme: 'dark',
  tabs: [],
  activeTabId: null,
  toolState: {},
  dockPosition: 'bottom',
  dockTools: DEFAULT_DOCK_TOOLS,
};

function pickPersisted(state: Store): PersistedPrefs {
  return {
    favorites: state.favorites,
    recents: state.recents,
    theme: state.theme,
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    toolState: state.toolState,
    dockPosition: state.dockPosition,
    dockTools: state.dockTools,
  };
}

let writeTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleWrite(state: Store) {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(() => {
    void storage.set(STORAGE_KEY, pickPersisted(state));
  }, 120);
}

function newTabId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `t_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export const usePrefs = create<Store>((set, get) => ({
  ...DEFAULTS,
  hydrated: false,

  openTool: (toolId) => {
    const { tabs } = get();
    // Reuse an existing tab for the same tool if it's already open.
    const existing = tabs.find((t) => t.toolId === toolId);
    if (existing) {
      set({ activeTabId: existing.id });
      scheduleWrite(get());
      return;
    }
    const tab: Tab = { id: newTabId(), toolId };
    const next = [...tabs, tab].slice(-MAX_TABS);
    set({ tabs: next, activeTabId: tab.id });
    scheduleWrite(get());
  },

  closeTab: (tabId) => {
    const { tabs, activeTabId } = get();
    const idx = tabs.findIndex((t) => t.id === tabId);
    if (idx < 0) return;
    const nextTabs = tabs.filter((t) => t.id !== tabId);
    let nextActive: string | null = activeTabId;
    if (activeTabId === tabId) {
      // Focus the neighbor on the right; fall back to the left; else null.
      nextActive = nextTabs[idx]?.id ?? nextTabs[idx - 1]?.id ?? null;
    }
    set({ tabs: nextTabs, activeTabId: nextActive });
    scheduleWrite(get());
  },

  setActiveTab: (tabId) => {
    set({ activeTabId: tabId });
    scheduleWrite(get());
  },

  closeAllTabs: () => {
    set({ tabs: [], activeTabId: null });
    scheduleWrite(get());
  },

  toggleFavorite: (id) => {
    const { favorites } = get();
    const next = favorites.includes(id)
      ? favorites.filter((f) => f !== id)
      : [...favorites, id];
    set({ favorites: next });
    scheduleWrite(get());
  },

  pushRecent: (id) => {
    const { recents } = get();
    const next = [id, ...recents.filter((r) => r !== id)].slice(0, MAX_RECENTS);
    set({ recents: next });
    scheduleWrite(get());
  },

  setTheme: (theme) => {
    set({ theme });
    scheduleWrite(get());
  },

  setToolState: (toolId, state) => {
    set({ toolState: { ...get().toolState, [toolId]: state } });
    scheduleWrite(get());
  },

  getToolState: (toolId) => {
    return get().toolState[toolId] as never;
  },

  setDockPosition: (position) => {
    set({ dockPosition: position });
    scheduleWrite(get());
  },

  setDockTools: (ids) => {
    set({ dockTools: ids });
    scheduleWrite(get());
  },

  hydrate: async () => {
    const stored = await storage.get<Partial<PersistedPrefs> & {
      activeToolId?: string | null;
    }>(STORAGE_KEY);
    if (stored) {
      // Migrate from the pre-tabs schema: if no `tabs` was stored but
      // `activeToolId` was, synthesize a single tab to keep continuity.
      let tabs: Tab[] = stored.tabs ?? [];
      let activeTabId: string | null = stored.activeTabId ?? null;
      if (tabs.length === 0 && stored.activeToolId) {
        const tab = { id: newTabId(), toolId: stored.activeToolId };
        tabs = [tab];
        activeTabId = tab.id;
      }
      set({
        favorites: stored.favorites ?? [],
        recents: stored.recents ?? [],
        theme: stored.theme ?? 'dark',
        tabs,
        activeTabId,
        toolState: stored.toolState ?? {},
        // Users whose persisted prefs predate the dock feature don't have
        // these fields. Treat them as new users for the dock specifically so
        // they get the v1.0 default experience.
        dockPosition: stored.dockPosition ?? DEFAULTS.dockPosition,
        dockTools:
          stored.dockTools === undefined ? DEFAULTS.dockTools : stored.dockTools,
        hydrated: true,
      });
    } else {
      set({ hydrated: true });
    }

    storage.onChange(STORAGE_KEY, (next) => {
      if (!next || typeof next !== 'object') return;
      const n = next as Partial<PersistedPrefs>;
      set({
        favorites: n.favorites ?? get().favorites,
        recents: n.recents ?? get().recents,
        theme: n.theme ?? get().theme,
        tabs: n.tabs ?? get().tabs,
        activeTabId: n.activeTabId ?? get().activeTabId,
        toolState: n.toolState ?? get().toolState,
        dockPosition: n.dockPosition ?? get().dockPosition,
        dockTools: n.dockTools === undefined ? get().dockTools : n.dockTools,
      });
    });
  },
}));
